import { reaction } from "mobx";
import { useEffect } from "react";
import loginState, { type LoginState } from "../../stores/auth/loginState";
import { reportError } from "../errorReporting";

// Steps the app moves through on its own, between page load and showing the login button or the logged-in shop
const LOADING_STATES = new Set<LoginState>([
   "START_UP",
   "VERIFY_SESSION",
   "CHECKING_LOGIN",
   "LOGGING_IN",
   "COMPANY_SELECTED",
   "FEATURES_LOADING",
   "DELIVERY_DATES_LOADING",
   "ASSORTMENT_LOADING",
   "NOTIFICATIONS_LOADING"
]);

const STUCK_STARTUP_TIMEOUT_MS = 30_000;

/**
 * Reports a loading step that has not finished after 30 seconds, since a hang throws no error of its own.
 */
export const useStuckStartupReporting = () => {
   useEffect(() => {
      let timeout: ReturnType<typeof setTimeout> | undefined;
      const dispose = reaction(
         () => loginState.currentState,
         (state) => {
            clearTimeout(timeout);
            if (LOADING_STATES.has(state)) {
               timeout = setTimeout(() => {
                  reportError(new Error(`Startup stuck in ${state} for ${STUCK_STARTUP_TIMEOUT_MS / 1000} seconds`), {
                     context: "Stuck startup",
                     $exception_fingerprint: `stuck-startup-${state}`
                  });
               }, STUCK_STARTUP_TIMEOUT_MS);
            }
         },
         { fireImmediately: true }
      );
      return () => {
         dispose();
         clearTimeout(timeout);
      };
   }, []);
};
